import React, { CSSProperties, useEffect, useState } from 'react';
import { Select } from 'src/antd';
import { useLanguages, useLocale } from '@transifex/react';
import { useCompany } from 'src/app/Company.store';
import { useUserMaybe, useUserStore } from 'src/app/User.store';
import { setDatetimeLocale } from 'src/utils';
import { tx } from '@transifex/native';
import { handleApiChange } from './shared/FormChangeApiUrl';

const LANGUAGES_MAP: Record<string, string> = {
  da: 'da',
  en: 'en',
  no: 'no_NO',
  fi: 'fi',
  sv: 'sv',
};

export const setTransifexLocale = (language: string = 'en'): Promise<void> => {
  const [lang] = language.toLowerCase().split('_');
  const code = LANGUAGES_MAP[lang] || lang;
  return tx.setCurrentLocale(code);
};

type TLanguageOption = { code: string; localized_name: string };

const normalizeCode = (raw: string | undefined): string =>
  (raw || '').toLowerCase().split(/[-_]/)[0];

/**
 * Filter Transifex's language list by the tenant's `supported_languages`.
 * - When `supported` has entries → return that intersection (default is
 *   always added so the fallback locale can never be hidden).
 * - When `supported` is undefined/empty AND `defaultLanguage` is set →
 *   return only the default; a tenant that hasn't enumerated supported
 *   languages should not implicitly expose every locale Transifex knows.
 * - Only when both are missing do we fall back to the full list (a
 *   genuinely unconfigured tenant — we still need to show *something*).
 * - Codes match case-insensitively on the leading language tag
 *   (`DA` / `da-DK` / `da_DK` / `da` all collapse to `da`).
 */
export const getVisibleLanguages = (
  languages: TLanguageOption[],
  supported: string[] | undefined,
  defaultLanguage: string | undefined
): TLanguageOption[] => {
  const fallback = normalizeCode(defaultLanguage);
  if (!supported || supported.length === 0) {
    if (!fallback) return languages;
    return languages.filter((l) => normalizeCode(l.code) === fallback);
  }
  const allowed = new Set(supported.map(normalizeCode));
  if (fallback) allowed.add(fallback);
  return languages.filter((l) => allowed.has(normalizeCode(l.code)));
};

/**
 * Whether `language` is one of the tenant's allowed locales. Used to decide
 * if a saved user preference is still valid for the current tenant.
 * - `supported` empty/undefined → only the default is allowed (mirrors
 *   `getVisibleLanguages`).
 */
export const isLanguageSupported = (
  language: string | undefined,
  supported: string[] | undefined,
  defaultLanguage: string | undefined
): boolean => {
  if (!language) return false;
  const candidate = normalizeCode(language);
  if (!supported || supported.length === 0) {
    const fallback = normalizeCode(defaultLanguage);
    return fallback ? candidate === fallback : true;
  }
  const allowed = new Set(supported.map(normalizeCode));
  const fallback = normalizeCode(defaultLanguage);
  if (fallback) allowed.add(fallback);
  return allowed.has(candidate);
};

export function LanguagePicker({ style }: { style?: CSSProperties }) {
  const { data: company } = useCompany();
  const userMaybe = useUserMaybe();
  const language = company?.default_language.toLowerCase();
  const supported = company?.supported_languages;
  const languages = useLanguages();
  const locale = useLocale();
  const [loading, setLoading] = useState(false);

  const visibleLanguages = getVisibleLanguages(
    languages,
    supported,
    company?.default_language
  );

  useEffect(() => {
    const userLang = userMaybe?.language?.toLowerCase();
    const savedLocale =
      (isLanguageSupported(userLang, supported, company?.default_language)
        ? userLang
        : undefined) ||
      language ||
      'en';
    setTransifexLocale(savedLocale);
    setDatetimeLocale(savedLocale);
  }, [language, userMaybe?.language, supported, company?.default_language]);

  const handleChange = async (v: string) => {
    setLoading(true);
    if (userMaybe) {
      // if there is a user then change the user language there.
      await useUserStore.getState().update({ language: v });
    }
    tx.setCurrentLocale(v);
    setLoading(false);
  };

  return (
    <Select
      onClick={handleApiChange}
      style={style}
      className="language-picker"
      onChange={handleChange}
      options={visibleLanguages.map(({ code, localized_name }: $TSFixMe) => ({
        value: code,
        label: localized_name,
      }))}
      value={locale}
      loading={loading}
    />
  );
}
